import { z } from 'zod';

/**
 * The app's single email rule, mirroring the backend's `@IsEmail()` — class-validator delegates to
 * validator.js `isEmail` with default options, so that function's rules are the contract.
 *
 * **Why not `z.email()`** (ADO 118281): zod 4's built-in allows only `'`, `+`, `-` and `_` out of
 * RFC 5322's nineteen `atext` specials and rejects the other fifteen — `! # $ % & * / = ? ^ ` { | }
 * ~`. The deployed API accepts every one of them (probed 2026-09-11 against
 * `POST /internal-users/admin`), so the client was refusing legal addresses the server would have
 * taken. Being *stricter* than the server is not a safe default here: it blocks real admins, and it
 * is invisible in testing because the happy path never uses those characters.
 *
 * The rules below are validator.js's, in its own order:
 *   - total length ≤ 254 (`ignore_max_length: false`);
 *   - exactly one unquoted `@` split, local part ≤ 64 (RFC 5321);
 *   - local part split on `.`, every segment non-empty and made of `atext` (plus U+00A1-FFFF,
 *     because `allow_utf8_local_part` defaults to true) — which is what rejects `a..b@`, `.a@`
 *     and `a.@` without a rule of their own;
 *   - domain is an FQDN: ≥ 2 labels, each 1-63 chars of `[a-z0-9-]` with no leading/trailing
 *     hyphen and no underscore (`allow_underscores: false`), and a TLD of ≥ 2 letters
 *     (`require_tld: true`), never all-digits.
 *
 * Quoted local parts (`"john doe"@x.com`) are deliberately **not** supported: validator.js accepts
 * them, no Greenway surface has ever needed one, and admitting the quoted grammar would widen the
 * parser far more than the bug requires.
 */

const MAX_TOTAL = 254;
const LOCAL_PART_MAX = 64;
const DOMAIN_LABEL_MAX = 63;

/** RFC 5322 `atext` + the UTF-8 range validator.js admits by default. */
const LOCAL_SEGMENT = /^[a-z\d!#$%&'*+\-/=?^_`{|}~¡-￿]+$/i;
const DOMAIN_LABEL = /^[a-z\d¡-￿-]+$/i;
const TLD = /^(?:[a-z¡-￿]{2,}|xn[a-z\d-]{2,})$/i;

function isFqdn(domain: string): boolean {
  const labels = domain.split('.');
  if (labels.length < 2) return false;
  const tld = labels[labels.length - 1];
  if (!TLD.test(tld) || /^\d+$/.test(tld)) return false;
  return labels.every(
    (label) => label.length <= DOMAIN_LABEL_MAX && DOMAIN_LABEL.test(label) && !/^-|-$/.test(label),
  );
}

/** True when the backend's `@IsEmail()` would accept `value`. Pure; unit-tested. */
export function isBackendCompatibleEmail(value: string): boolean {
  if (value.length === 0 || value.length > MAX_TOTAL) return false;
  const at = value.lastIndexOf('@');
  if (at <= 0 || at === value.length - 1) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (local.length > LOCAL_PART_MAX) return false;
  if (!local.split('.').every((segment) => LOCAL_SEGMENT.test(segment))) return false;
  return isFqdn(domain);
}

/** `z.email()`'s replacement: one message for every malformed-address case, as the ACs specify. */
export function emailAddressSchema(message: string) {
  return z.string().refine(isBackendCompatibleEmail, message);
}
